import React, { useState, useEffect, useRef } from "react";
import { downloadCsv } from "../common/sharedFunctions";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from "common";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams,useLocation } from "react-router-dom";
import { colors } from "../components/Theme/WebTheme";
import { Box, Grid, Typography  } from "@mui/material";
import AlertDialog from "../components/AlertDialog";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import Switch from "@mui/material/Switch";
import moment from 'moment';
import {MAIN_COLOR} from "../common/sharedFunctions"
import EditIcon from '@mui/icons-material/Edit';
import localization from '../components/Table/Localization';
import { hasUserPermission } from "../common/userPermissions";
import { canDoAction, showActionPermissionDenied } from "common/src/actions/userPermissions";
import AdminDataTable from "../components/AdminDataTable";
import AdminStatusBadge from "../components/AdminStatus";
import { ActionButton, SectionHeader, StatusBadge, SurfaceCard } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";

const truthyStatusValues = ["true", "1", "yes", "active", "approved", "enabled", "on"];

const isTruthyStatus = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") return truthyStatusValues.includes(value.trim().toLowerCase());
  return false;
};

const adminTableResponsiveCss = `
  .garahex-admin-table-responsive,
  .garahex-users-table-responsive {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    box-sizing: border-box;
  }
  .garahex-admin-table-responsive .MuiPaper-root,
  .garahex-users-table-responsive .MuiPaper-root {
    width: 100%;
    min-width: 900px;
    box-sizing: border-box;
  }
  .garahex-admin-table-responsive table,
  .garahex-users-table-responsive table {
    min-width: 900px;
    border-collapse: collapse;
  }
  .garahex-admin-table-responsive .MuiTableCell-root,
  .garahex-users-table-responsive .MuiTableCell-root {
    font-size: 12px !important;
    padding: 8px 10px !important;
    white-space: nowrap;
  }
  .garahex-admin-table-responsive .MuiToolbar-root,
  .garahex-users-table-responsive .MuiToolbar-root {
    min-width: 900px;
  }
`;

export default function Users() {
  const navigate = useNavigate();
  const {id} = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    editUser,
    deleteUser,
    fetchUsersOnce,
  } = api;
  const settings = useSelector((state) => state.settingsdata.settings);
  const [data, setData] = useState([]);
  const [sortedData, SetSortedData] = useState([]);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const auth = useSelector((state) => state.auth);
  const isFleetAdmin = auth && auth.profile && String(auth.profile.usertype).toLowerCase() === "fleetadmin";
  const dispatch = useDispatch();
  const loaded = useRef(false);
  const {state} = useLocation()
  const [currentPage,setCurrentPage] = useState(null)
  
  useEffect(()=>{
    setCurrentPage(state?.pageNo)
  },[state])
  
  const HandalePageChange = (page)=>{
    setCurrentPage(page)
  }
  
  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  useEffect(() => {
    if (staticusers) {
      setData(
        staticusers.filter(
          (user) => {
            if (user.usertype !== "customer") return false;
            if (auth.profile.usertype === "admin") return true;
            if (auth.profile.usertype !== "fleetadmin") return false;
            return user.fleetadmin === auth.profile.uid ||
              user.createdBy === auth.profile.uid ||
              !!(user.fleetadmins && user.fleetadmins[auth.profile.uid]);
          }
        )
      );
    } else {
      setData([]);
    }
    loaded.current = true;
  }, [staticusers, auth.profile]);

  useEffect(()=>{
    if(data){
      SetSortedData(data.sort((a,b)=>(moment(b.createdAt) - moment(a.createdAt))))
    }
  },[data,sortedData])

  const columns = [
    { title: t('createdAt'), field: 'createdAt', editable:'never', defaultSort:'desc',render: rowData => rowData.createdAt? moment(rowData.createdAt).format('lll'):null},

    { title: t("first_name"), field: "firstName" },
    { title: t("last_name"), field: "lastName" },
    { title: "Company Name", field: "companyName", render: rowData => rowData.companyName || "" },
    {
      title: t("mobile"),
      field: "mobile",
      editable: "onAdd",
      render: (rowData) =>
        settings.AllowCriticalEditsAdmin ? rowData.mobile : t("hidden_demo"),
    },
    {
      title: t("email"),
      field: "email",
      editable: "onAdd",
      render: (rowData) =>
        settings.AllowCriticalEditsAdmin ? rowData.email : t("hidden_demo"),
    },
    {
      title: t("profile_image"),
      field: "profile_image",
      render: (rowData) =>
        rowData.profile_image ? (
          <img
            alt="Profile"
            src={rowData.profile_image}
            style={{ width: 40, height: 40, borderRadius: "50%" }}
          />
        ) : (
          <AccountCircleIcon sx={{ fontSize: 40 }} />
        ),
      editable: "never",
    },
    {
      title: t("account_approve"),
      field: "approved",
      type: "boolean",
      render: (rowData) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AdminStatusBadge status={isTruthyStatus(rowData.approved) ? "ACTIVE" : "PENDING"} />
          <Switch
            disabled={!settings.AllowCriticalEditsAdmin}
            checked={isTruthyStatus(rowData.approved)}
            onChange={() => handelApproved(rowData)}
          />
        </Box>
      ),
    },
  ];


  const [selectedRow, setSelectedRow] = useState(null);

  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: "" });
  };

  const userActionAllowed = (action) => canDoAction({
        role: auth && auth.profile && auth.profile.usertype,
        moduleKey: "users",
        action,
        accessPermissions: settings && settings.accessPermissions,
        fallback: hasUserPermission(settings, "customer", action)
    });
    const canAddUser = userActionAllowed("add");
    const canEditUser = userActionAllowed("edit");
    const canViewUser = userActionAllowed("view");
    const canDeleteUser = userActionAllowed("delete");
  const tableActions = [
    canAddUser && {
      icon: "add",
      tooltip: t("add_customer"),
      isFreeAction: true,
      onClick: (event) => navigate("/users/edituser/customer",{state:{pageNo:currentPage}}),
    },
    canEditUser && ((rowData) => ({
      tooltip: t("edit"),
      icon: () => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            marginRight:0
          }}
        >
          <EditIcon />
        </div>
      ),
      onClick: (event, rowData) =>{
        navigate(`/users/edituser/customer/${rowData.id}`,{state:{pageNo:currentPage}})
      }
    })),
    canViewUser && {
      icon: "info",
      tooltip: t("profile_page_subtitle"),
      onClick: (event, rowData) => {
        navigate(`/users/customerdetails/${rowData.id}`,{state:{pageNo:currentPage}});
      },
    },
    canViewUser && ((rowData) => ({
      icon: () => (
        <div style={{display:"flex", alignItems:"center" , flexWrap :"wrap", padding:10, backgroundColor:MAIN_COLOR, borderRadius:5, }}>
          <Typography style={{color:colors.LandingPage_Background}}>
          {t("documents")}
          </Typography>
          </div>
      ),
      tooltip:t('documents'),
      onClick:()=>navigate(`/users/userdocuments/${id}/${rowData.id}`,{state:{pageNo:currentPage}})
    }))
  ].filter(Boolean);

  const handelApproved = (rowData) => {
        if (!canEditUser) {
            showActionPermissionDenied();
            return rowData;
        }
    const updatedUser = { ...rowData, approved: !isTruthyStatus(rowData.approved) };
    dispatch(editUser(updatedUser.id, updatedUser));
    dispatch(fetchUsersOnce());
    return updatedUser;
  };

  return !loaded.current ? (
    <CircularLoading />
  ) : (
    <Box style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      <style>{adminTableResponsiveCss}</style>
      {isFleetAdmin ? (
        <Box style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <ActionButton
            onClick={() => navigate("/users/edituser/customer", { state: { pageNo: currentPage } })}
          >
            ADD CLIENTS
          </ActionButton>
        </Box>
      ) : null}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <SurfaceCard sx={{ minHeight: 110 }}>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>Clients</Typography>
            <Typography sx={{ color: adminTokens.colors.text, fontSize: "1.8rem", fontWeight: 950 }}>{sortedData.length}</Typography>
          </SurfaceCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <SurfaceCard sx={{ minHeight: 110 }}>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>Active</Typography>
            <Typography sx={{ color: adminTokens.colors.success, fontSize: "1.8rem", fontWeight: 950 }}>{sortedData.filter((item) => isTruthyStatus(item.approved)).length}</Typography>
          </SurfaceCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <SurfaceCard sx={{ minHeight: 110 }}>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>Pending</Typography>
            <Typography sx={{ color: adminTokens.colors.warning, fontSize: "1.8rem", fontWeight: 950 }}>{sortedData.filter((item) => !isTruthyStatus(item.approved)).length}</Typography>
          </SurfaceCard>
        </Grid>
      </Grid>
      <SurfaceCard>
        <SectionHeader
          title={t("riders_title")}
          description="Client records, contact details, profile images, documents, and approval controls use the existing account workflow."
          actions={<StatusBadge tone="info">{`${sortedData.length} records`}</StatusBadge>}
        />
      <AdminDataTable
        title={t("riders_title")}
        columns={columns}
        onChangePage={(page)=>HandalePageChange(page)}
        style={{
          direction: isRTL === "rtl" ? "rtl" : "ltr",
        }}
        data={sortedData}
        emptyTitle="No clients found"
        emptyDescription="No client records match the current account scope."
        onRowClick={(evt, selectedRow) =>
          setSelectedRow(selectedRow.tableData.id)
        }
        options={{
          initialPage:state?.pageNo,
          exportCsv: (columns, data) => {
            let hArray = [];
            const headerRow = columns.map((col) => {
              if (typeof col.title === "object") {
                return col.title.props.text;
              }
              hArray.push(col.field);
              return col.title;
            });
            const dataRows = data.map(({ tableData, ...row }) => {
              row.createdAt =
                new Date(row.createdAt).toLocaleDateString() +
                " " +
                new Date(row.createdAt).toLocaleTimeString();
              let dArr = [];
              for (let i = 0; i < hArray.length; i++) {
                dArr.push(row[hArray[i]]);
              }
              return Object.values(dArr);
            });
            const { exportDelimiter } = ",";
            const delimiter = exportDelimiter ? exportDelimiter : ",";
            const csvContent = [headerRow, ...dataRows]
              .map((e) => e.join(delimiter))
              .join("\n");
            const csvFileName = "download.csv";
            downloadCsv(csvContent, csvFileName);
          },
          exportButton: {
            csv: settings.AllowCriticalEditsAdmin,
            pdf: false,
          },
          maxColumnSort: "all_columns",
          rowStyle: (rowData) => ({
            backgroundColor:
              selectedRow === rowData.tableData.id ? "rgba(11, 124, 255, 0.09)" :colors.WHITE
          }),
        }}
        localization={localization(t)}
        editable={canDeleteUser ? {
          onRowDelete: (oldData) =>
            settings.AllowCriticalEditsAdmin
              ? new Promise((resolve) => {
                  setTimeout(() => {
                    resolve();
                    dispatch(deleteUser(oldData.id));
                    dispatch(fetchUsersOnce());
                  }, 600);
                })
              : new Promise((resolve) => {
                  setTimeout(() => {
                    resolve();
                    alert(t("demo_mode"));
                  }, 600);
                }),
        } : {}}
        actions={tableActions}
      />
      </SurfaceCard>
      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>
        {commonAlert.msg}
      </AlertDialog>
    </Box>
  );
}
